import get from 'lodash/get'
import moment from 'moment'

import {
  getUserProfile,
  getPositionsForUser,
  updateUserProfile,
  getUserByInvitation,
  getPositionsForNewUser,
  postUserByInvitation,
  acceptChangeSchoolInvitation,
  postExternalUserByInvitation,
  updateUserAuthorProfile
} from '../../services/UserService'
import { getSubjects } from '../../services/SubjectService'
import { newGetEducationalLevels } from '../../services/EducationalLevelService'
import { isError } from '../../utils/api'
import { isValidEmail } from '../../utils/validators'
import { getSchoolInfo, saveSchoolInfo } from '../../services/SchoolService'
import { uploadFile } from '../../services/FileUploadService'
import { deepCopy } from '../../utils/object'
import { getAllSchoolsInSchoolGroup, saveSchoolGroupInfo } from '../../services/SchoolGroupService'
import router, { routes } from '../../router'
import { baseNewUser } from '../../utils/constants'

export default {
  state: {
    selectedSchool: {
      id: null,
      name: ''
    },
    userProfile: {
      userVO: {},
      schoolGroupVO: {}
    },
    newUser: deepCopy(baseNewUser),
    subjects: [],
    educationalLevels: [],
    userPositions: [],
    schoolGroupEdition: {},
    userProfileEdition: {},
    editionModified: false,
    errorOnSubmit: false,
    errors: {
      type: null
    },
    schoolsInSchoolGroup: []
  },

  mutations: {
    setUserProfile(state, profile) {
      state.userProfile = profile
    },
    setProfileEdition(state) {
      state.userProfileEdition = { ...state.userProfile.userVO }
    },
    updateProfileField(state, { field, value }) {
      state.editionModified = true
      state.userProfileEdition[field] = value
    },
    setNewUserProfile(state, profile) {
      if (!profile.userVO.educationalLevels || !profile.userVO.educationalLevels.length) {
        profile.userVO.educationalLevels = profile.schoolEducationalLevels
      } else {
        profile.userVO.educationalLevels = profile.userVO.educationalLevels.filter(level => profile.schoolEducationalLevels.includes(level))
      }
      state.newUser = { ...profile, password: '', confirmPassword: '' }
    },
    changeNewUserPosition(state, { field, value }) {
      state.newUser.userVO[field].positionId = value
    },
    changeNewUserVOField(state, { field, value }) {
      state.newUser.userVO[field] = value
    },
    changeNewUserField(state, { field, value }) {
      state.newUser[field] = value
    },
    setEducationalLevels(state, educationalLevels) {
      state.educationalLevels = educationalLevels
    },
    setSubjects(state, subjects) {
      state.subjects = subjects
    },
    setUserPositions(state, userPositions) {
      state.userPositions = userPositions
    },
    setSchoolInfo(state, schoolData) {
      state.userProfile.schoolGroupVO.currentSchool = schoolData
      state.selectedSchool.id = schoolData.id
      state.selectedSchool.name = schoolData.name
    },
    setSchoolEdition(state) {
      state.schoolGroupEdition = deepCopy(state.userProfile.schoolGroupVO)
    },
    updateSchoolEditionField(state, { field, value }) {
      state.editionModified = true
      state.schoolGroupEdition.currentSchool[field] = value
    },
    updateSchoolGroupEditionField(state, { field, value }) {
      state.editionModified = true
      state.schoolGroupEdition[field] = value
    },
    clearEdition(state) {
      state.editionModified = false
      state.schoolGroupEdition = {}
      state.userProfileEdition = {}
      state.errorOnSubmit = false
      state.errors.type = null
    },
    setSchoolGroupVO(state, schoolGroup) {
      state.userProfile.schoolGroupVO = schoolGroup
    },
    setEditionModified(state) {
      state.editionModified = true
    },
    setToastError(state, type) {
      state.errors.type = type
      state.errorOnSubmit = true
    },
    hydrateSchoolsInSchoolGroup(state, data) {
      state.schoolsInSchoolGroup = data
    }
  },
  actions: {
    hydrateProfileView({ commit, dispatch }) {
      commit('show', { element: 'fullPageLoader' })
      dispatch('hydrateSubjects')
      dispatch('hydrateEducationalLevels')
      dispatch('hydrateProfile')
      dispatch('hydrateUserPositions')
      commit('hide', { element: 'fullPageLoader' })
    },
    changeNewUserField({ commit }, payload) {
      commit('changeNewUserField', payload)
    },
    changeNewUserVOField({ commit }, payload) {
      commit('changeNewUserVOField', payload)
    },
    changeNewUserPosition({ commit }, payload) {
      commit('changeNewUserPosition', payload)
    },
    async hydrateRegisterView({ commit, dispatch }) {
      commit('show', { element: 'fullPageLoader' })
      dispatch('hydrateSubjects')
      dispatch('hydrateEducationalLevels')
      dispatch('hydrateNewUser')
      dispatch('hydrateUserPositions')
      commit('hide', { element: 'fullPageLoader' })
    },
    async hydrateProfile({ commit }) {
      const response = await getUserProfile().catch(e => e.response)
      if (!isError(response.status)) commit('setUserProfile', response.data)
      else commit('setUserProfile', {})
    },
    async hydrateNewUser({ commit, state }, payload) {
      commit('show', { element: 'fullPageLoader' })
      const response = await getUserByInvitation(payload).catch(e => e.response)
      const success = !isError(response.status)
      commit('hide', { element: 'fullPageLoader' })
      if (!success) router.push({ name: routes.usedToken })
      else {
        commit('setNewUserProfile', response.data)
        if (state.newUser.expired) {
          router.push({
            name: routes.expiredToken,
            params: { userId: state.newUser.userVO.userId, schoolId: state.newUser.schoolId }
          })
        }
      }
    },
    async hydrateSubjects({ commit }) {
      const response = await getSubjects().catch(e => e.response)
      if (!isError(response.status)) commit('setSubjects', response.data)
      else commit('setSubjects', {})
    },
    async hydrateSubjectsNewUser({ commit }) {
      const response = await getSubjects().catch(e => e.response)
      if (!isError(response.status)) commit('setSubjects', response.data)
      else commit('setSubjects', {})
    },
    async hydrateEducationalLevels({ commit }) {
      const response = await newGetEducationalLevels().catch(e => e.response)
      if (!isError(response.status)) commit('setEducationalLevels', response.data)
      else commit('setEducationalLevels', {})
    },
    async hydrateUserPositions({ commit }) {
      const response = await getPositionsForUser().catch(e => e.response)
      if (!isError(response.status)) commit('setUserPositions', response.data)
      else commit('setUserPositions', [])
    },
    async hydrateNewUserPositions({ commit }, payload) {
      const response = await getPositionsForNewUser(payload).catch(e => e.response)
      if (!isError(response.status)) {
        commit('setUserPositions', response.data)
      } else commit('setUserPositions', [])
    },
    async hydrateSchoolInfo({ commit }, schoolId) {
      const response = await getSchoolInfo(schoolId).catch(e => e.response)
      const isSuccessful = !isError(response.status)
      if (isSuccessful) commit('setSchoolInfo', response.data)
      return isSuccessful
    },
    async hydrateSchoolsInSchoolGroup({ commit }, schoolGroupId) {
      const response = await getAllSchoolsInSchoolGroup(schoolGroupId).catch(e => e.response)
      const isSuccessful = !isError(response.status)
      if (isSuccessful) commit('hydrateSchoolsInSchoolGroup', response.data)
      return isSuccessful
    },
    async createSchoolEdition({ dispatch, commit, state }) {
      if (!get(state, 'userProfile.schoolGroupVO.currentSchool')) await dispatch('hydrateProfile')
      commit('setSchoolEdition')
    },
    updateSchoolEditionField({ commit }, data) {
      commit('updateSchoolEditionField', data)
    },
    async createProfileEdition({ dispatch, commit, state }) {
      commit('show', { element: 'fullPageLoader' })
      if (!Object.keys(state.userProfile.userVO).length) await dispatch('hydrateProfile')
      commit('setProfileEdition')
      commit('hide', { element: 'fullPageLoader' })
    },
    async updateProfileInfo({ dispatch, commit }, data) {
      const response = await updateUserProfile(data).catch(e => e.response)
      const isSuccessful = !isError(response.status)
      if (isSuccessful) {
        await dispatch('hydrateProfile')
        commit('clearEdition')
      }
      return isSuccessful
    },
    async updateAuthorProfile({ commit }, data) {
      const response = await updateUserAuthorProfile(data).catch(e => e.response)
      const isSuccessful = !isError(response.status)
      return isSuccessful
    },
    updateSchoolGroupEditionField({ commit }, data) {
      commit('updateSchoolGroupEditionField', data)
    },
    updateProfileField({ commit }, data) {
      commit('updateProfileField', data)
    },
    async uploadUserAvatar({ commit }, file) {
      const response = await uploadFile({
        content: file.content,
        extension: file.fileType,
        filePath: `Users/${file.userId}/profile_${moment().format('YYYYMMDDHHmm')}`
      }).catch(e => e.response)
      const success = !isError(response.status)
      return { success, data: response.data }
    },
    async updateProfileInfoWithFile({ commit, dispatch }, { data, file }) {
      const response = await uploadFile({
        content: file.content,
        extension: file.fileType,
        filePath: `Users/${file.userId}/profile_${moment().format('YYYYMMDDHHmm')}`
      }).catch(e => e.response)
      if (!isError(response.status)) {
        data.avatar = response.data
        commit('clearEdition')
        return dispatch('updateProfileInfo', data)
      }
    },
    clearEdition({ commit }) {
      commit('clearEdition')
    },
    async saveSchoolEdition({ dispatch, commit, state }) {
      const { name: schoolName, address: schoolAddress, email } = state.schoolGroupEdition.currentSchool
      if (!schoolName || !schoolAddress) {
        commit('setToastError', 'requiredFields')
      } else if (!isValidEmail(email)) {
        commit('setToastError', 'email')
      } else {
        await dispatch('saveEdition', { saveService: saveSchoolInfo, data: state.schoolGroupEdition.currentSchool, mutation: 'setSchoolInfo' })
      }
    },
    async saveSchoolGroupEdition({ dispatch, commit, state }) {
      const { name: schoolName, address: schoolAddress, email } = state.schoolGroupEdition.currentSchool
      if (!state.schoolGroupEdition.name || !schoolName || !schoolAddress) {
        commit('setToastError', 'requiredFields')
      } else if (!isValidEmail(email)) {
        commit('setToastError', 'email')
      } else {
        await dispatch('saveEdition', { saveService: saveSchoolGroupInfo, data: state.schoolGroupEdition, mutation: 'setSchoolGroupVO' })
      }
    },
    async saveEdition({ commit, state }, { saveService, data, mutation }) {
      const response = await saveService(data)
      if (!isError(response.status)) {
        commit(mutation, response.data)
        commit('clearEdition')
      }
    },
    setToastError({ commit }, type) {
      commit('setToastError', type)
    },
    async submitNewUser({ commit }) {
      const response = await postUserByInvitation(this.state.profile.newUser).catch(e => e.response)
      return !isError(response.status)
    },
    async submitNewExternalUser({ commit }) {
      const response = await postExternalUserByInvitation(this.state.profile.newUser)
      return !isError(response.status)
    },
    async submitSchoolChange({ commit }, data) {
      const response = await acceptChangeSchoolInvitation(data)
      return !isError(response.status)
    },
    setEditionModified({ commit }) {
      commit('setEditionModified')
    },
    async uploadSchoolImage({ commit }, { base64, extension, schoolId }) {
      commit('show', { element: 'fullPageLoader' })
      const response = await uploadFile({
        content: base64,
        extension,
        filePath: `Schools/${schoolId}/profile_${moment().format('YYYYMMDDHHmm')}`
      }).catch(e => e.response)
      const value = !isError(response.status) ? response.data : ''
      commit('updateSchoolEditionField', { field: 'avatar', value })
      commit('hide', { element: 'fullPageLoader' })
    },
    async uploadSchoolGroupImage({ commit }, { base64, extension, schoolGroupId }) {
      commit('show', { element: 'fullPageLoader' })
      const response = await uploadFile({
        content: base64,
        extension,
        filePath: `SchoolGroup/${schoolGroupId}/profile_${moment().format('YYYYMMDDHHmm')}`
      }).catch(e => e.response)
      const value = !isError(response.status) ? response.data : ''
      commit('updateSchoolGroupEditionField', { field: 'picture', value })
      commit('hide', { element: 'fullPageLoader' })
    },
    async hydrateSchoolInEdition({ commit, dispatch }, schoolId) {
      await dispatch('hydrateSchoolInfo', schoolId)
      commit('setSchoolEdition')
    }
  },
  getters: {
    // TODO: Check if this other getters need to be removed
    getSchoolGroup: state => state.userProfile.schoolGroupVO,
    getSchool: state => state.userProfile.schoolGroupVO && state.userProfile.schoolGroupVO.currentSchool
  }
}
